import {useQuery} from "react-query";
import {StripeService} from "crqlar-react-common/dist/services/integrations/stripe/stripe.service";
import {Box, Chip, Divider, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import {LoadingButton} from "@mui/lab";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {formatFullDMY} from "crqlar-react-common/dist/utils/date.util";
import SettingsPageHeader from "../layout/SettingsPageHeader";
import Property from "crqlar-react-common/dist/components/Property";
import PricingPageCrqlar from "./PricingPageCrqlar";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {useNavigate} from "react-router-dom";

const Subscription = () => {

    const {t} = useLocalize()
    const navigate = useNavigate()

    const readOnlyUser = useReadOnly()

    const currentSubscriptionQuery = useQuery(
        ['currentSubscription'],
        async () => {
            return await StripeService.getCurrentSubscription()
        }
    )

    const portalSessionQ = useQuery(
        'portalSession',
        async () => {
            const portalSession = await StripeService.createPortalSession(window.location.href)
            // open in same tab
            window.open(portalSession, '_self')
        }, {
            enabled: false
        })

    const loading = currentSubscriptionQuery.isLoading
    const subscription = currentSubscriptionQuery.data
    const hasSubscription = subscription?.status === 'active'
    const canceled = !!subscription?.cancelAtPeriodEnd

    const subscriptionSection = hasSubscription ? (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('You have an active subscription')}
            </Typography>

            <Stack gap={1}>
                <Property title={t('Status')}>
                    <Chip
                        icon={canceled ? <CancelIcon/> : <CheckCircleIcon/>}
                        label={t(canceled ? 'Canceled' : 'Active')}
                        color={canceled ? 'warning' : 'success'}
                        variant={'outlined'}
                        size={'small'}
                    />
                </Property>

                <Property title={t('Period start')}>
                    <Typography variant={'body1'}>
                        {formatFullDMY(subscription?.currentPeriodStart) || '-'}
                    </Typography>
                </Property>

                <Property title={t('Period end')}>
                    <Typography variant={'body1'}>
                        {formatFullDMY(subscription?.currentPeriodEnd) || '-'}
                    </Typography>
                </Property>

                <Stack direction={'row'} justifyContent={'flex-start'}>
                    <LoadingButton
                        disabled={readOnlyUser}
                        loading={portalSessionQ.isLoading}
                        onClick={() => portalSessionQ.refetch()}
                        variant={'contained'}
                    >
                        {t('Manage subscription')}
                    </LoadingButton>
                </Stack>
            </Stack>
        </Stack>
    ) : (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('You are not subscribed')}
            </Typography>

            <PricingPageCrqlar/>
        </Stack>
    )

    const portalSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Invoices and Billing info')}
            </Typography>

            <Stack direction={'row'} justifyContent={'flex-start'}>
                <LoadingButton
                    disabled={readOnlyUser}
                    loading={portalSessionQ.isLoading}
                    onClick={() => portalSessionQ.refetch()}
                    variant={'outlined'}
                >
                    {t('View')}
                </LoadingButton>
            </Stack>
        </Stack>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            <SettingsPageHeader
                title={t('Billing')}
                back={() => navigate('/system')}
                loading={loading}
            />

            {
                !loading &&
                <Stack
                    sx={{
                        width: '100%',
                        maxWidth: '900px',
                        mx: 'auto',
                        mt: 6,
                        gap: 6,
                    }}
                >
                    {subscriptionSection}
                    <Divider/>
                    {portalSection}
                </Stack>
            }
        </Box>
    )

}

export default Subscription
